import { strapiEndpoints } from "@/config/strapi-fetch.config";
import { fetchFromStrapi } from "@/utils/graphql/client";
import { STRAPI_URL } from "@/constants/envConstants";

const useDynamicFetch = import.meta.env.VITE_APP_STRAPI_DYNAMIC === "true";

const assetMapModules = import.meta.glob("../data/asset-map.json", { eager: true });
const assetMap: Record<string, string> =
    (Object.values(assetMapModules)[0] as { default: Record<string, string> })?.default ?? {};

let strapiContentCache: Record<string, unknown> | null = null;

async function getStaticContent(): Promise<Record<string, unknown>> {
    if (!strapiContentCache) {
        const modules = import.meta.glob("../data/strapi-content.json", { eager: true, import: "default" });
        strapiContentCache = modules["../data/strapi-content.json"] as Record<string, unknown>;
    }
    return strapiContentCache;
}

/**
 * Get the Strapi base URL, automatically detecting the hostname in browser
 * This allows the app to work on local network devices without hardcoding IPs
 */
export const getStrapiBaseUrl = (): string => {
    if (typeof window !== "undefined") {
        const hostname = window.location.hostname;
    }

    let strapiUrl: string;
    if (typeof process !== "undefined" && process.env?.VITE_APP_STRAPI_URL) {
        strapiUrl = process.env.VITE_APP_STRAPI_URL;
    } else {
        strapiUrl = STRAPI_URL;
    }

    return strapiUrl;
};

/**
 * Get content from Strapi with Zod validation.
 * - Dynamic mode (dev / build:dev): fetches via Apollo, validates with Zod
 * - Static mode (build): loads from prebaked JSON (already validated at build time)
 */
export async function getStrapiContent<T>(key: string): Promise<T> {
    const config = strapiEndpoints.find((e) => e.key === key);

    if (!config) {
        throw new Error(`Strapi endpoint not configured for key: ${key}`);
    }

    if (useDynamicFetch) {
        const data = await fetchFromStrapi(config.query, config.variables);
        return data as T;
    }

    const content = await getStaticContent();
    const data = content[key];

    if (data === undefined) {
        throw new Error(`No prebaked content found for key: ${key}`);
    }

    return data as T;
}

/**
 * Resolve a Strapi media path to the correct URL.
 * - Dynamic mode: returns full Strapi URL
 * - Static mode: returns local path from asset map
 *
 * Appends ?updatedAt= cache-buster when updatedAt is provided.
 */
export function getStrapiMediaUrl(strapiPath: string | null | undefined, updatedAt?: string | null): string {
    if (!strapiPath) return "";

    const baseUrl = useDynamicFetch
        ? `${import.meta.env.VITE_APP_STRAPI_URL}${strapiPath}`
        : (assetMap[strapiPath] ?? strapiPath);

    if (updatedAt) {
        return `${baseUrl}?updatedAt=${encodeURIComponent(updatedAt)}`;
    }

    return baseUrl;
}